import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationItem from "./NotificationItem";
import { useTranslationsContext } from "../../../contexts/TranslationsContext";
import { ROUTES } from "../../../constants/routes";
import type { Notification } from "../types";

/* ── time-group helpers ─────────────────────────────────────────────────── */

type TimeGroup = { label: string; items: Notification[] };

function groupByTime(
  notifications: Notification[],
  labels: { today: string; yesterday: string; earlier: string },
): TimeGroup[] {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);

  const groups: Record<string, Notification[]> = {
    today: [],
    yesterday: [],
    earlier: [],
  };

  for (const n of notifications) {
    const d = new Date(n.createdAt);
    if (d >= todayStart) groups.today.push(n);
    else if (d >= yesterdayStart) groups.yesterday.push(n);
    else groups.earlier.push(n);
  }

  const result: TimeGroup[] = [];
  if (groups.today.length) result.push({ label: labels.today, items: groups.today });
  if (groups.yesterday.length) result.push({ label: labels.yesterday, items: groups.yesterday });
  if (groups.earlier.length) result.push({ label: labels.earlier, items: groups.earlier });
  return result;
}

/* ── keyframes (injected once) ──────────────────────────────────────────── */

const KEYFRAMES_ID = "notify-drawer-keyframes";

function ensureKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById(KEYFRAMES_ID)) return;
  const style = document.createElement("style");
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes notifySlideIn {
      from { opacity: 0; transform: translateX(12px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @keyframes notifyFadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes notifyPulse {
      0%, 100% { opacity: 0.5; }
      50%      { opacity: 0.25; }
    }
  `;
  document.head.appendChild(style);
}

/* ── component ─────────────────────────────────────────────────────────── */

type NotificationDrawerProps = {
  open: boolean;
  onClose: () => void;
  notifications: Notification[];
  onMarkRead: (id: number) => void;
  onMarkAllRead: () => void;
  onDelete: (id: number) => void | Promise<void>;
};

export default function NotificationDrawer({
  open,
  onClose,
  notifications,
  onMarkRead,
  onMarkAllRead,
  onDelete,
}: NotificationDrawerProps) {
  const { translation } = useTranslationsContext();
  const navigate = useNavigate();

  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());

  const handleDelete = useCallback(
    async (id: number) => {
      setDeletingIds((prev) => new Set(prev).add(id));
      try {
        await onDelete(id);
      } finally {
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [onDelete],
  );

  const hasUnread = notifications.some((n) => !n.read);

  const timeLabels = useMemo(
    () => ({
      today: translation.NOTIFY_GROUP_TODAY?.defaultMessage || "Today",
      yesterday: translation.NOTIFY_GROUP_YESTERDAY?.defaultMessage || "Yesterday",
      earlier: translation.NOTIFY_GROUP_EARLIER?.defaultMessage || "Earlier",
    }),
    [translation],
  );

  const groups = useMemo(
    () => groupByTime(notifications, timeLabels),
    [notifications, timeLabels],
  );

  // Inject keyframes stylesheet
  if (open) ensureKeyframes();

  // Running index for stagger animation
  let itemIdx = 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 370 },
          maxWidth: "100vw",
          backgroundColor: "#1a1726",
          backgroundImage:
            "linear-gradient(180deg, rgba(122,172,179,0.03) 0%, transparent 40%)",
          borderLeft: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.95rem",
              color: "#e0ddf0",
              letterSpacing: "-0.01em",
            }}
          >
            {translation.NOTIFY_TITLE?.defaultMessage || "Notifications"}
          </Typography>
          {notifications.length > 0 && (
            <Box
              sx={{
                fontSize: "0.65rem",
                color: "#7aacb3",
                backgroundColor: "rgba(122,172,179,0.12)",
                borderRadius: "8px",
                px: 0.8,
                py: 0.15,
                fontWeight: 600,
                lineHeight: 1.5,
              }}
            >
              {notifications.length}
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          {hasUnread && (
            <Button
              size="small"
              onClick={onMarkAllRead}
              startIcon={<DoneAllIcon sx={{ fontSize: "14px !important" }} />}
              sx={{
                textTransform: "none",
                fontSize: "0.7rem",
                color: "#7aacb3",
                minWidth: "auto",
                px: 1,
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: "rgba(122,172,179,0.08)",
                },
              }}
            >
              {translation.NOTIFY_MARK_ALL_READ?.defaultMessage ||
                "Mark all read"}
            </Button>
          )}
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: "#6a6680",
              "&:hover": { color: "#e0ddf0" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* subtle divider line */}
      <Box
        sx={{
          height: "1px",
          mx: 1.5,
          background:
            "linear-gradient(90deg, transparent, rgba(122,172,179,0.15), transparent)",
        }}
      />

      {/* ── List ───────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        {notifications.length === 0 ? (
          /* ── Empty state ─────────────────────────── */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 10,
              px: 4,
              animation: "notifyFadeIn 0.4s ease",
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(122,172,179,0.08)",
                border: "1px solid rgba(122,172,179,0.12)",
                mb: 2,
              }}
            >
              <NotificationsNoneIcon
                sx={{ fontSize: 26, color: "#5e5a74" }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "0.85rem",
                color: "#6a6680",
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              {translation.NOTIFY_EMPTY?.defaultMessage ||
                "No notifications yet"}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.72rem",
                color: "#4e4a64",
                textAlign: "center",
              }}
            >
              {translation.NOTIFY_EMPTY_SUB?.defaultMessage ||
                "Class reminders, homework alerts, and more will appear here"}
            </Typography>
          </Box>
        ) : (
          /* ── Time-grouped items ──────────────────── */
          groups.map((group) => (
            <Box key={group.label}>
              {/* Section header */}
              <Box
                sx={{
                  px: 1.75,
                  pt: 1.5,
                  pb: 0.5,
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: "#1a1726ee",
                  backdropFilter: "blur(8px)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#5e5a74",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  {group.label}
                </Typography>
              </Box>
              {group.items.map((n) => {
                const delay = itemIdx * 40;
                itemIdx += 1;
                return (
                  <NotificationItem
                    key={n.id}
                    notification={n}
                    onMarkRead={onMarkRead}
                    onDelete={handleDelete}
                    isDeleting={deletingIds.has(n.id)}
                    animDelay={delay}
                  />
                );
              })}
            </Box>
          ))
        )}
      </Box>

      {/* ── Footer ─────────────────────────────────────── */}
      <Box
        sx={{
          height: "1px",
          mx: 1.5,
          background:
            "linear-gradient(90deg, transparent, rgba(122,172,179,0.12), transparent)",
          flexShrink: 0,
        }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 1.25,
          flexShrink: 0,
        }}
      >
        <Button
          size="small"
          startIcon={<SettingsIcon sx={{ fontSize: "14px !important" }} />}
          onClick={() => {
            onClose();
            navigate(ROUTES.NOTIFY_SETTINGS);
          }}
          sx={{
            textTransform: "none",
            fontSize: "0.72rem",
            color: "#6a6680",
            borderRadius: "8px",
            "&:hover": {
              color: "#7aacb3",
              backgroundColor: "rgba(122,172,179,0.06)",
            },
          }}
        >
          {translation.NOTIFY_SETTINGS_TITLE?.defaultMessage ||
            "Notification settings"}
        </Button>
      </Box>
    </Drawer>
  );
}
